import { Info, Video } from "@tamagui/lucide-icons";
import { useToastController } from "@tamagui/toast";
import { useRouter } from "expo-router";
import { useState } from "react";
import { ScrollView, XStack, YStack } from "tamagui";

import {
  ProductOption,
  type UserProgressionStateModel,
  type VisitOptionSelectionMetadata,
  type VisitType,
} from "@/api/generated/models";
import { useGetVisitOptionsForAFlow } from "@/api/queries/visitOptions";
import {
  Button,
  Header,
  Heading,
  LinkText,
  LoadingSpinner,
  Text,
} from "@/components";
import { useFeatureFlag } from "@/components/FeatureFlagProvider";
import { useUserProgression } from "@/conversation/sidebar/UserProgression/UserProgressionContext";
import { useAuthState } from "@/hooks/useAuthState";
import { useLocalPartnerName } from "@/hooks/useLocalPartnerName";
import { captureException } from "@/sentry";
import PaymentModeToggle, { PaymentMode } from "./PaymentModeToggle";
import {
  DISABLED_REASONS,
  usePatientResponsibility,
} from "./usePatientResponsibility";
import VisitOptionCard from "./VisitOptionCard";
import {
  type OfferProductOption,
  type VisitOption,
  getPriceLabel,
  getVisitOptions,
} from "./visitOptions";

export type VisitOptionsScreenProps = {
  onContinue?: (args?: { metadata?: VisitOptionSelectionMetadata }) => void;
  onSelectOption?: (option: VisitOption) => void;
  onActivateMembership?: () => void;
  isLoadingVisitType?: VisitType;
  chosenProductOption?: ProductOption;
};

const getVisitTypePreferenceForOption = (option: VisitOption) =>
  option.visitType === "live_video" ? "audiovisual" : "chat";

const getPaymentProductOption = (
  chosenProductOption: ProductOption | undefined,
  productOptions: OfferProductOption[],
  visitType: VisitType,
) =>
  chosenProductOption === ProductOption.all_you_can_eat_subscription
    ? chosenProductOption
    : productOptions.find(
        (product) =>
          product.type === "one-time" &&
          product.visit_types?.includes(visitType),
      )?.code;

export default function VisitOptionsScreen({
  onContinue,
  onSelectOption,
  onActivateMembership,
  isLoadingVisitType,
  chosenProductOption,
}: VisitOptionsScreenProps) {
  const router = useRouter();
  const { show: showToast } = useToastController();
  const { isSignedIn } = useAuthState();
  const progression = useUserProgression()?.progression;
  const flowName = progression?.flow_name ?? "";
  // The async paywall narrows its options to this clinic request's modality; other flows have none.
  const clinicRequestId = progression?.clinic_request_id ?? undefined;
  // The progression narrows to the modalities it has since determined are allowed (e.g. an
  // A/V-required state collapses live options to video-only).
  const { data, isLoading, isError } = useGetVisitOptionsForAFlow({
    flowName,
    clinicRequestId,
    userProgressionId: progression?.id,
  });
  const { productOptions = [], visitOptions = [] } = data ?? {};

  const activeVisitOptions = isError ? [] : visitOptions;
  const { options, partnerName: sponsoredPartnerName } = getVisitOptions(
    activeVisitOptions,
    isError ? [] : productOptions,
  );

  const localPartnerName = useLocalPartnerName();
  const anonLocalPartnerName = isSignedIn ? null : localPartnerName;
  const partnerName = sponsoredPartnerName ?? anonLocalPartnerName ?? undefined;

  const isEntitledVisit = activeVisitOptions.some(
    (o) => o.entitlement_state.entitled,
  );

  const freeByLocalPartner = anonLocalPartnerName != null;
  const isFreeVisit = isEntitledVisit || Boolean(partnerName);
  const { value: isInsurancePayEnabled } = useFeatureFlag(
    "insurance-pay",
    false,
  );
  const responsibility = usePatientResponsibility({
    enabled: !isLoading && Boolean(isInsurancePayEnabled) && !isFreeVisit,
  });
  const [userSelectedMode, setUserSelectedMode] = useState<PaymentMode | null>(
    null,
  );

  const { patientResponsibilityCents } = responsibility;
  const cashPayPrices = options.flatMap((option) =>
    option.entitled || option.cashPayCents === null
      ? []
      : [option.cashPayCents],
  );
  const cheapestCashPayCents =
    cashPayPrices.length > 0 ? Math.min(...cashPayPrices) : null;
  // Copay only prices live video (see isDisabledForMode), so Insurance can't lead without it.
  const hasLiveVideoOption = options.some(
    (option) => option.visitType === "live_video",
  );
  // While the check is in flight we stay on Insurance so the price labels resolve in place
  // instead of flipping cash -> insurance under the user.
  const insuranceLeads =
    hasLiveVideoOption &&
    (responsibility.isLoading ||
      (cheapestCashPayCents !== null &&
        patientResponsibilityCents !== null &&
        patientResponsibilityCents < cheapestCashPayCents));
  const paymentMode: PaymentMode = responsibility.isInsuranceEnabled
    ? (userSelectedMode ??
      (insuranceLeads ? PaymentMode.insurance : PaymentMode.cash))
    : PaymentMode.cash;

  if (flowName && isLoading) {
    return (
      <LoadingSpinner
        size="$900"
        flex={1}
        alignSelf="center"
        justifyContent="center"
      />
    );
  }

  const handleSelect = (option: VisitOption) => {
    if (onSelectOption) {
      onSelectOption(option);
      return;
    }
    if (onContinue) {
      const isFreeVisit = Boolean(partnerName) || option.entitled;
      const paymentProductOption = getPaymentProductOption(
        chosenProductOption,
        productOptions,
        option.visitType,
      );
      // A selectable paid modality should always have a matching product in the API response.
      // Without one, there is no valid product to send to checkout.
      if (!paymentProductOption) {
        captureException("Missing product option for visit selection", {
          extra: {
            visitType: option.visitType,
            isFreeVisit,
            chosenProductOption,
            productOptionCount: productOptions.length,
          },
        });
        showToast(
          "We couldn’t start your visit. Please try again, or contact support if the problem continues.",
        );
        return;
      }

      // Sponsored / entitled visits don't run through the insurance-vs-cash decision, so we
      // omit `payment_mode` for them — downstream (e.g. the consent screen's AOB row) reads
      // its absence as "not applicable" instead of misinterpreting a stale toggle value.
      // Only an actual insurance selection carries the eligibility check-run id; forwarding it
      // anchors checkout to the exact run the displayed responsibility was quoted from.
      const isInsuranceSelection =
        !isFreeVisit && paymentMode === PaymentMode.insurance;
      onContinue({
        metadata: {
          type: "visit_option_selection",
          payment_visit_type: option.visitType,
          payment_product_option: paymentProductOption,
          visit_type_preference: getVisitTypePreferenceForOption(option),
          ...(isFreeVisit ? {} : { payment_mode: paymentMode }),
          ...(isInsuranceSelection
            ? { insurance_eligibility_check_id: responsibility.runId }
            : {}),
        },
      });
      return;
    }
  };

  const headingText = partnerName
    ? `You are sponsored by ${partnerName}!`
    : "Choose your visit type";

  const isInsuranceLoading =
    paymentMode === PaymentMode.insurance && responsibility.isLoading;

  // Bridge's patient_responsibility currently only covers live_video, so other visit types are
  // disabled in Insurance mode until the backend returns per-visit-type reponsibility.
  const isDisabledForMode = (option: VisitOption) =>
    paymentMode === PaymentMode.insurance &&
    !option.entitled &&
    option.visitType !== "live_video";

  const renderPriceLabel = (option: VisitOption) => {
    if (freeByLocalPartner) {
      return "Free";
    }
    if (isInsuranceLoading && !option.entitled) {
      return <LoadingSpinner size="$150" />;
    }
    return getPriceLabel(
      option,
      paymentMode,
      responsibility.patientResponsibilityCents,
    );
  };

  const reason = responsibility.isInsuranceEnabled
    ? null
    : responsibility.disabledReason;
  const disabledReason =
    !isSignedIn && reason === DISABLED_REASONS.noInsurance ? null : reason;

  const showPaymentMode =
    isInsurancePayEnabled && !partnerName && !isEntitledVisit;

  return (
    <YStack flex={1}>
      <Header paddingHorizontal="$250">
        <YStack gap="$150">
          <XStack
            alignItems="flex-start"
            justifyContent={showPaymentMode ? "space-between" : "flex-start"}
            gap="$150"
            $maxLg={{ flexDirection: "column", alignItems: "flex-start" }}
          >
            <Heading size="$xl">{headingText}</Heading>
            {showPaymentMode && (
              <YStack
                gap="$50"
                alignItems="flex-end"
                $maxLg={{ alignItems: "flex-start" }}
              >
                <PaymentModeToggle
                  value={paymentMode}
                  onChange={setUserSelectedMode}
                  insuranceDisabled={!responsibility.isInsuranceEnabled}
                />
                {disabledReason && (
                  <DisabledReasonMessage
                    reason={disabledReason}
                    onOpenInsuranceSettings={() =>
                      router.setParams({ settings: "insurance" })
                    }
                  />
                )}
              </YStack>
            )}
          </XStack>
          <ReselectionNotice progression={progression} />
        </YStack>
      </Header>
      <ScrollView
        flex={1}
        contentContainerStyle={{
          flexGrow: 1,
          gap: "$250",
          padding: "$250",
        }}
      >
        <YStack flex={1} gap="$250">
          <XStack gap="$200" width="100%" $maxLg={{ flexDirection: "column" }}>
            {options.map((option) => (
              <VisitOptionCard
                key={option.visitType}
                option={option}
                priceLabel={renderPriceLabel(option)}
                loading={isLoadingVisitType === option.visitType}
                disabled={isDisabledForMode(option)}
                selectDisabled={isInsuranceLoading}
                onPress={() => handleSelect(option)}
              />
            ))}
          </XStack>
        </YStack>

        {!partnerName && onActivateMembership && (
          <XStack flexWrap="wrap" justifyContent="center" gap="$50">
            <Text size="$sm" color="$textSecondary" textAlign="center">
              Does your work or school pay for Curai Health?
            </Text>
            <Button.Wrapper
              dataTrackingId="product-options-activate-membership-link"
              onPress={onActivateMembership}
              aria-label="Activate membership"
            >
              <LinkText size="$sm" bold>
                Activate membership
              </LinkText>
            </Button.Wrapper>
          </XStack>
        )}
      </ScrollView>
    </YStack>
  );
}

// One banner for every reselection reason. Self-gating: the reason clears on a legal re-pick, and
// this only mounts on selection steps, so no current_step check is needed.
function ReselectionNotice({
  progression,
}: {
  progression?: UserProgressionStateModel;
}) {
  const metadata = progression?.metadata;
  // Fall back to the legacy repriced_for_established_patient flag so progressions already reset
  // before this key was unified keep their banner across the deploy.
  const reason =
    metadata?.payment_reselection_reason ??
    (metadata?.repriced_for_established_patient === true
      ? "established_repricing"
      : undefined);
  if (reason !== "av_required" && reason !== "established_repricing")
    return null;
  const Icon = reason === "av_required" ? Video : Info;
  return (
    <XStack
      alignItems="center"
      gap="$150"
      backgroundColor="$fillClinician"
      borderColor="$strokePrompt"
      borderWidth="$strokeWidth"
      borderRadius="$150"
      padding="$200"
    >
      <Icon size={20} color="$fillPrimary" flexShrink={0} />
      <Text size="$sm" flex={1}>
        {reason === "av_required" ? (
          <>
            In your state, your first visit needs to be on video.{" "}
            <Text size="$sm" bold>
              You'll be able to choose video or chat next time.
            </Text>
          </>
        ) : (
          <>
            Your visit price may have updated.{" "}
            <Text size="$sm" bold>
              Please confirm your visit below.
            </Text>
          </>
        )}
      </Text>
    </XStack>
  );
}

type DisabledReasonMessageProps = {
  reason: string;
  onOpenInsuranceSettings: () => void;
};

function DisabledReasonMessage({
  reason,
  onOpenInsuranceSettings,
}: DisabledReasonMessageProps) {
  if (reason === DISABLED_REASONS.noInsurance) {
    return (
      <Text size="$xs" color="$textSecondary">
        Add insurance in{" "}
        <LinkText
          size="$xs"
          onPress={onOpenInsuranceSettings}
          aria-label="Open insurance settings"
        >
          Settings
        </LinkText>{" "}
        to see insurance pricing
      </Text>
    );
  }
  return (
    <Text size="$xs" color="$textSecondary">
      {reason}
    </Text>
  );
}
